export interface QuartermasterPassengerLists {
	onboard: any[];
	expected: any[];
	departing: any[];
}

export interface QuartermasterInTransitItem {
	carrierID: string;
	expectedCount: number;
	departingCount: number;
	carrierName: string;
	legsArray: any[];
	classType: string;
}

function sortPobs(pobA: any, pobB: any) {
	const pobAName = `${pobA?.lastName || ''}${pobA?.firstName || ''}`;
	const pobBName = `${pobB?.lastName || ''}${pobB?.firstName || ''}`;
	return pobAName.localeCompare(pobBName);
}

function normalizeNameSearch(value: string) {
	return (value || '')
		.trim()
		.toLocaleLowerCase()
		.replace(/[\s,]+/g, ' ');
}

function matchesPobName(pob: any, normalizedSearchValue: string) {
	if (!normalizedSearchValue) return true;

	const firstName = pob?.firstName || '';
	const lastName = pob?.lastName || '';
	const firstNameFirst = normalizeNameSearch(`${firstName} ${lastName}`);
	const lastNameFirst = normalizeNameSearch(`${lastName} ${firstName}`);

	return (
		firstNameFirst.includes(normalizedSearchValue) ||
		lastNameFirst.includes(normalizedSearchValue)
	);
}

function matchesSelectedRig(pob: any, rigID: string) {
	return Boolean(
		rigID && pob?.lastKnownController?._id && `RIG-${pob.lastKnownController._id}` === rigID
	);
}

function carrierGoesToRig(carrier: any, rigID: string) {
	if (!carrier || !rigID) return false;
	if (!Array.isArray(carrier.legsArray)) return true;
	return carrier.legsArray.some((leg: any) => `RIG-${leg?.destinationID}` === rigID);
}

export function selectQuartermasterViewData(
	pobNodeData: any[],
	rigID: string,
	carrierID: string,
	searchValue: string,
	pobSearchValue = ''
) {
	const pobsForRig = pobNodeData.filter((pob) => matchesSelectedRig(pob, rigID)).sort(sortPobs);

	const onboard = pobsForRig.filter(
		(pob) => pob.checkInType === 'ONBOARD' || pob.checkInType === 'DEPARTING'
	);
	const expectedForRig = pobsForRig.filter((pob) => pob.checkInType === 'EXPECTED');
	const departingForRig = pobsForRig.filter((pob) => pob.checkInType === 'DEPARTING');

	const expected = carrierID
		? expectedForRig.filter((pob) => pob?.currentCarrierID?._id === carrierID)
		: expectedForRig;
	const departing = carrierID
		? departingForRig.filter((pob) => pob?.currentCarrierID?._id === carrierID)
		: departingForRig;
	const normalizedPobSearchValue = normalizeNameSearch(pobSearchValue);
	const filteredOnboard = onboard.filter((pob) =>
		matchesPobName(pob, normalizedPobSearchValue)
	);
	const filteredExpected = expected.filter((pob) =>
		matchesPobName(pob, normalizedPobSearchValue)
	);
	const filteredDeparting = departing.filter((pob) =>
		matchesPobName(pob, normalizedPobSearchValue)
	);

	const carriers = new Map<
		string,
		{ carrier: any; expectedCount: number; departingCount: number }
	>();
	expectedForRig.concat(departingForRig).forEach((pob) => {
		const carrier = pob?.currentCarrierID;
		if (!carrier?._id || !carrierGoesToRig(carrier, rigID)) return;

		const existing = carriers.get(carrier._id);
		carriers.set(carrier._id, {
			carrier,
			expectedCount:
				(existing?.expectedCount || 0) + (pob.checkInType === 'EXPECTED' ? 1 : 0),
			departingCount:
				(existing?.departingCount || 0) + (pob.checkInType === 'DEPARTING' ? 1 : 0),
		});
	});

	const normalizedSearchValue = searchValue?.trim().toLocaleLowerCase();
	const inTransit: QuartermasterInTransitItem[] = Array.from(carriers.values())
		.map(({ carrier, expectedCount, departingCount }) => ({
			carrierID: carrier._id,
			expectedCount,
			departingCount,
			carrierName: carrier.desig,
			legsArray: carrier.legsArray,
			classType: carrier.classType,
		}))
		.filter(
			(item) =>
				!normalizedSearchValue ||
				item.carrierName?.toLocaleLowerCase().includes(normalizedSearchValue)
		);

	return {
		passengerLists: {
			onboard: filteredOnboard,
			expected: filteredExpected,
			departing: filteredDeparting,
		} as QuartermasterPassengerLists,
		inTransit,
	};
}
